/**
 * アプリケーション全体で使用される定数定義
 * マジックナンバーを避け、一元管理するための設定ファイル
 */

/**
 * ページネーション関連の定数
 */
export const PAGINATION = {
  // 動画一覧の1ページあたりの表示件数
  VIDEO_LIST_PAGE_SIZE: 12,
  // お気に入り・いいね動画の1ページあたりの表示件数（全件取得用）
  FAVORITE_LIST_PAGE_SIZE: 1000,
  // 通知一覧の1ページあたりの表示件数
  NOTIFICATION_PAGE_SIZE: 20,
  // コメント一覧の1ページあたりの表示件数
  COMMENT_PAGE_SIZE: 20,
} as const;

/**
 * API関連の定数
 */
export const API = {
  // リクエストタイムアウト（ミリ秒）
  TIMEOUT: 30000,
  // リトライ回数
  MAX_RETRIES: 3,
} as const;

/**
 * ファイルアップロード関連の定数
 *
 * MAX_FILE_SIZE_GB / MAX_FILE_SIZE_BYTES の上限は 20GB（仮。Issue #411）。
 * バックエンド側の上限（backend/src/config/uploadLimits.ts）と値を揃えること。
 *
 * VIDEO_UPLOAD_TIMEOUT は 30分にしている。
 * axiosInstance（services/api.ts）はログイン・一覧・コメント等ほぼ全APIが通る共通インスタンスで、
 * 分割アップロード（services/chunkUploadService.ts）は fetch/XMLHttpRequest でこの axios を経由しない
 * （xhr.timeout はチャンク数に応じて別途算出）。ここを延ばしても20GB対応の効果は無く、
 * 他APIの失敗検出が遅れるだけなので、ファイルサイズ上限とは独立に 30分とする。
 */
export const UPLOAD = {
  // 動画アップロードのタイムアウト（30分 = 1800000ms）
  VIDEO_UPLOAD_TIMEOUT: 1800000,
  // 最大同時アップロードファイル数
  MAX_CONCURRENT_UPLOADS: 5,
  // 動画アップロードのファイルサイズ上限（GB）
  MAX_FILE_SIZE_GB: 20,
  // 動画アップロードのファイルサイズ上限（バイト）
  MAX_FILE_SIZE_BYTES: 20 * 1024 * 1024 * 1024,
} as const;

/**
 * UI関連の定数
 */
export const UI = {
  // アニメーション時間（ミリ秒）
  ANIMATION_DURATION: 300,
  // デバウンス時間（ミリ秒）
  DEBOUNCE_DELAY: 500,
  // ケバブメニューのz-index
  KEBAB_MENU_Z_INDEX: 99999,
  // ケバブメニューの左オフセット
  KEBAB_MENU_LEFT_OFFSET: {
    DEFAULT: 0,
    SIDE_AND_MOBILE: 120,
  },
} as const;

/**
 * 外部動画の同期ステータスバッジ定義
 * - synced:  同期元からの取得が完了し、再生可能な状態
 * - pending: 同期元を検出済みだが、動画ファイルの取得がまだ完了していない
 * - missing: 同期元にファイルが存在しない（削除・移動された等）
 * - error:   同期処理中にエラーが発生した
 */
export const EXTERNAL_STATUS_MAP: Record<string, { label: string; variant: string }> = {
  synced:  { label: '同期済', variant: 'synced' },
  pending: { label: '同期待ち', variant: 'pending' },
  missing: { label: '欠落', variant: 'missing' },
  error:   { label: 'エラー', variant: 'error' },
} as const;

/**
 * 外部動画関連のメッセージ定数
 */
export const EXTERNAL_VIDEO_MESSAGES = {
  FETCH_ERROR: '外部動画の取得に失敗しました',
  RELATED_FETCH_ERROR: '関連動画の取得に失敗しました',
  NO_RELATED_VIDEOS: '関連動画がありません。',
} as const;

/**
 * 外部動画のアップローダー識別子
 */
export const EXTERNAL_UPLOADER_ID = 'external' as const;

/**
 * フォールバックラベル定数
 */
export const FALLBACK_LABELS = {
  UNTITLED: '無題',
  ANONYMOUS: '匿名',
  EXTERNAL_HOSPITAL: '院外共有',
  NO_AFFILIATION: '所属なし',
} as const;
